import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloseIcon from "@mui/icons-material/Close";

import useMetaMask from "../contexts/useMetaMask";
import { MainChainID } from "../utils/constants";

// Canonical popup CTA button spec (see EFTActionButton's ActionButtonNew/CancelButton) —
// reused here so the "Switch network" CTA matches the rest of the app's action buttons.
const SwitchNetworkButton = styled(Button)({
  height: 40,
  minWidth: 130,
  backgroundColor: "black",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  border: "none",
  padding: "11px 14px 12px 14px",
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

const SwitchNetworkProcessingButton = styled(Button)({
  height: 40,
  minWidth: 130,
  backgroundColor: "#333",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  border: "none",
  "&:hover": {
    border: "none",
    backgroundColor: "#333",
  },
});

// Global wrong-network guard (V9.1 UX spec §7): every on-chain surface assumes BSC
// testnet (chainId 97) — a mismatched wallet makes direct transactions fail with opaque
// errors and would sign relay typed-data against the wrong EIP-712 domain. Rendered on
// every screen (fixed to the top) whenever a wallet is connected to another chain.
// switchChain falls back to wallet_addEthereumChain when the wallet doesn't know BSC.
// Dismissable, but the dismissal is scoped to the offending chain: switching to yet
// another wrong network (or reconnecting) re-arms the warning rather than leaving the
// user silently on a chain where every transaction will fail.
const NetworkGuardBanner = () => {
  const { isConnected, isOnCorrectChain, switchChain, chainId } = useMetaMask();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Re-arm on any chain/connection change, so landing on a different wrong network
  // (or reconnecting) warns again instead of staying silently hidden.
  useEffect(() => {
    setDismissed(false);
    setError(null);
  }, [chainId, isConnected]);

  if (!isConnected || isOnCorrectChain || dismissed) {
    return null;
  }

  const handleSwitch = async () => {
    setBusy(true);
    setError(null);
    const result = await switchChain();
    if (typeof result === "string") {
      setError(result);
    }
    setBusy(false);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "12px",
        padding: "10px 16px",
        backgroundColor: "#B00020",
        color: "white",
        fontFamily: "'Inter', sans-serif",
        fontSize: "14px",
      }}
    >
      <WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />
      <span>
        Wrong network — this app runs on BNB Smart Chain Testnet (chain {MainChainID})
        {chainId ? `, but this site's wallet connection is on chain ${parseInt(chainId, 16)}` : ""}.
        {error ? ` ${error}` : ""}
      </span>
      {busy ? (
        <SwitchNetworkProcessingButton disabled variant="contained">
          <CircularProgress size={20} sx={{ color: "white" }} />
        </SwitchNetworkProcessingButton>
      ) : (
        <SwitchNetworkButton onClick={handleSwitch} variant="contained">
          Switch network
        </SwitchNetworkButton>
      )}
      <IconButton
        aria-label="Dismiss wrong network warning"
        onClick={() => setDismissed(true)}
        sx={{
          color: "white",
          padding: "4px",
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.15)" },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default NetworkGuardBanner;
